/* eslint-disable camelcase */
import React from 'react'
import { PrismicRichText } from '@prismicio/react'
import type { CollectionProducts } from '@lib/types'
import ProductCardSneakPeak from '@components/prismic/ProductCardSneakPeak'
import type { SliceComponentProps } from '@prismicio/react'
import type { Content } from '@prismicio/client'

export type DefaultSubsDetailProductsListProps =
	SliceComponentProps<Content.SubsDetailProductsListSlice> & {
		collectionProducts?: CollectionProducts
	}

function useTailwindColumns() {
	const [columns, setColumns] = React.useState(5)
	React.useEffect(() => {
		function updateColumns() {
			const width = window.innerWidth
			if (width >= 1024) setColumns(5)
			else if (width >= 768) setColumns(4)
			else if (width >= 640) setColumns(3)
			else setColumns(2)
		}
		updateColumns()
		window.addEventListener('resize', updateColumns)
		return () => window.removeEventListener('resize', updateColumns)
	}, [])
	return columns
}

export default function DefaultSubsDetailProductsList({
	slice,
	collectionProducts = []
}: DefaultSubsDetailProductsListProps) {
	const { primary } = slice
	const { section_title, section_subtitle } = primary
	const ctaText = primary.cta_text
	const ctaLink = primary.cta_link

	const columns = useTailwindColumns()
	const products = collectionProducts || []

	// Desktop slider state
	const maxVisible = columns
	const [startIdx, setStartIdx] = React.useState(0)
	const canScrollLeft = startIdx > 0 && products.length > maxVisible
	const canScrollRight = startIdx + maxVisible < products.length && products.length > maxVisible

	const handlePrev = () => {
		if (products.length <= maxVisible) return
		setStartIdx((idx) => {
			if (idx === 0) {
				const lastPageStart = Math.floor((products.length - 1) / maxVisible) * maxVisible
				return lastPageStart
			}
			return Math.max(0, idx - maxVisible)
		})
	}
	const handleNext = () => {
		if (products.length <= maxVisible) return
		setStartIdx((idx) => {
			if (idx >= products.length - maxVisible) return 0
			return idx + maxVisible
		})
	}

	const visibleProducts = products.slice(startIdx, startIdx + maxVisible)

	return (
		<section
			data-slice-type={slice.slice_type}
			data-slice-variation={slice.variation}
			id="sneak-peek"
			className="w-full bg-white py-16 font-sans scroll-mt-[62px] md:scroll-mt-[76px]"
		>
			<div className="max-w-7xl mx-auto">
				<div className="px-4 text-center mb-2">
					<span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-bbx-spark font-semibold mb-3">
						<i className="fa-solid fa-circle text-[6px]" />
						Updated monthly
						<i className="fa-solid fa-circle text-[6px]" />
					</span>
				</div>
				<div className="px-4 text-3xl lg:text-4xl text-center font-serif tracking-tight capitalize text-bbx-style">
					<PrismicRichText field={section_title} />
				</div>
				{section_subtitle && (
					<div className="px-4 text-base mt-4 md:whitespace-pre-line text-center max-w-xl mx-auto text-bbx-style/60">
						<PrismicRichText field={section_subtitle} />
					</div>
				)}
				{/*
					Mobile swipe carousel — CSS scroll-snap.
					Each card is (100vw - 56px) wide:
					  16px left padding on the track keeps the first card off the edge,
					  16px gap between cards,
					  1/3 of the next card peeks in from the right:
					    card = (100vw - 32px) * 3/4 = 75vw - 24px
				*/}
				<div
					className="md:hidden mt-14 flex overflow-x-auto snap-x snap-mandatory gap-4 pr-6"
					style={{ scrollbarWidth: 'none' }}
				>
					{products.map((product, i) => (
						<div
							key={product.id}
							className={`snap-start flex-shrink-0 w-[calc(75vw-24px)]${i === 0 ? ' ml-4' : ''}`}
						>
							<ProductCardSneakPeak product={product} />
						</div>
					))}
				</div>
				{/* Product slider */}
				<div className="hidden md:flex relative items-center mt-10 mx-4 bg-white px-8 py-10">
					{/* Left arrow - bold filled chevron, more space */}
					<button
						onClick={handlePrev}
						disabled={!canScrollLeft}
						className={`z-10 w-12 h-12 flex items-center justify-center transition-opacity duration-200 ${
							!canScrollLeft ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:opacity-80 cursor-pointer'
						}`}
						aria-label="Scroll left"
						type="button"
					>
						<span aria-hidden="true" className="slider-chevron-left" />
					</button>
					<div className="flex-1 overflow-hidden">
						<div className="flex gap-x-7 justify-center">
							{visibleProducts.map((product) => (
								<ProductCardSneakPeak
									key={product.id}
									product={product}
									className="w-full h-full"
								/>
							))}
						</div>
					</div>
					{/* Right arrow - bold filled chevron, more space */}
					<button
						onClick={handleNext}
						disabled={!canScrollRight}
						className={`z-10 w-12 h-12 flex items-center justify-center transition-opacity duration-200 ${
							!canScrollRight ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:opacity-80 cursor-pointer'
						}`}
						aria-label="Scroll right"
						type="button"
					>
						<span aria-hidden="true" className="slider-chevron-right" />
					</button>
				</div>
				{ctaLink && ctaText ? (
					<div className="px-4 w-full flex justify-center mt-14">
						<a
							className="button button-dark-blue py-5 px-12 max-w-fit-content"
							href={ctaLink}
							type="button"
						>
							{ctaText}
						</a>
					</div>
				) : null}
			</div>
		</section>
	)
}